import React from "react";

import { useRecoilState } from "recoil";
import { rowSelected } from "recoilState/GlobalState";

import { generateColumns } from "services/Local-services";

import { DataGridPro as DataGrid, DataGridPro, esES, LicenseInfo } from "@mui/x-data-grid-pro";
// import { DataGrid } from "@mui/x-data-grid";

import { LICENSE_KEY } from "consts/env";

import classes from "./XGrid.module.scss";

LicenseInfo.setLicenseKey(LICENSE_KEY);

/**
 * Alto de CADA PISO del header cuando hay columnas agrupadas.
 *
 * El header total mide `headerHeight * (1 + niveles de grupo)`. Con un nivel, el
 * default de 56 lo dejaria en 112px sobre filas de 36 —tres filas de alto solo de
 * encabezado—; con 32 queda en 64, apenas mas que una grilla sin agrupar.
 */
const ALTO_PISO_HEADER = 32;

/**
 * Los campos que cuelgan de algun grupo, aplanados.
 *
 * Se baja en profundidad porque `children` admite grupos adentro de grupos: un nodo
 * con `children` es otro grupo, uno sin `children` es una columna.
 */
const camposAgrupados = (nodos = []) =>
    nodos.reduce(
        (campos, nodo) =>
            campos.concat(
                nodo.children ? camposAgrupados(nodo.children) : nodo.field
            ),
        []
    );

/**
 * Estira las columnas SIN grupo para que ocupen los DOS pisos del header.
 *
 * Con el header agrupado hay dos pisos, pero cada celda vive en uno solo: las columnas
 * sin grupo caen en el de abajo, con un hueco vacio encima, y sus titulos se ven
 * pegados al piso. En vez de subirlas, se las hace altas de verdad: ocupan los dos
 * pisos, el titulo se centra SOLO —la celda ya es flex con `align-items: center`— y
 * el borde derecho pasa a cruzar todo el header.
 *
 * De ahi sale el dibujo tipo planilla: linea entera entre todas las columnas y media
 * linea entre U y Q, que es la unica division que cae POR DENTRO de un grupo. En el
 * limite del grupo —a la derecha de Q— la linea tambien queda entera sin hacer nada:
 * el borde de "Mesas" cae justo encima del de Q y entre las dos completan el alto.
 *
 * El `!important` pisa el `height` INLINE que DataGrid le pone a cada celda (ver
 * GridGenericColumnHeaderItem). A un style inline no le gana una clase: era esto o
 * dibujar el borde a mano con un pseudo-elemento, duplicando el color del tema.
 *
 * El selector se apoya en algo que ya hace DataGrid: marca las celdas de COLUMNA con
 * `data-field` (singular) y las de GRUPO con `data-fields` (plural). O sea que
 * `[data-field]` por si solo ya deja afuera a los titulos de grupo, que son los que
 * tienen que quedarse en el piso de arriba.
 */
const columnasDeAltoCompleto = (columnGroups) => {
    const bajoUnGrupo = camposAgrupados(columnGroups)
        .map((field) => `:not([data-field="${field}"])`)
        .join("");

    return {
        [`& .MuiDataGrid-columnHeader[data-field]${bajoUnGrupo}`]: {
            height: `${ALTO_PISO_HEADER * 2}px !important`,
            // Se mete en el piso de arriba, que para estas columnas esta vacio.
            marginTop: `-${ALTO_PISO_HEADER}px`
        }
    };
};

export default function XGrid({
    rows,
    titles,
    secondarySelected,
    setSecondarySelected,
    loading,
    checkboxSelection = false,
    // Header de dos pisos: un titulo padre sobre columnas contiguas. Ver el bloque
    // de abajo. Sin este prop la grilla se comporta igual que siempre.
    columnGroups,
    // Clase para la fila entera, cuando una fila significa algo distinto de las demas.
    // Recibe los params de DataGrid y devuelve la clase, o nada.
    rowClassName
}) {
    const [selected, setSelected] = useRecoilState(rowSelected);

    const { row } = secondarySelected ? secondarySelected : selected;
    const setter = setSecondarySelected ? setSecondarySelected : setSelected;

    const columns = generateColumns(titles);

    return (
        <DataGridPro
            columns={columns}
            {...(columnGroups
                ? {
                    /**
                     * Agrupacion de columnas: un header padre sobre varias columnas.
                     *
                     * En la v5 de DataGrid es experimental y esta detras de la bandera.
                     * Sin ella, `columnGroupingModel` se ignora EN SILENCIO: no tira
                     * warning, simplemente no agrupa.
                     *
                     * Todo esto se aplica SOLO cuando hay grupos: una grilla sin agrupar
                     * conserva el alto de header default y no recibe ningun `sx`.
                     */
                    columnGroupingModel: columnGroups,
                    experimentalFeatures: { columnGrouping: true },
                    headerHeight: ALTO_PISO_HEADER,
                    /**
                     * Cambia las rayitas por bordes de verdad.
                     *
                     * La rayita default es un icono SVG suelto, centrado en su celda y
                     * siempre del mismo alto: con dos pisos quedaba desfasada entre las
                     * columnas de arriba y las de abajo. Esto la apaga (sigue siendo el
                     * agarre para redimensionar, solo que invisible) y en su lugar le
                     * pone `border-right` a cada celda, que si acompaña su alto.
                     *
                     * Ademas DataGrid ya lo tiene contemplado para headers agrupados:
                     * con esto le dibuja el borde de abajo a "Mesas".
                     */
                    showColumnRightBorder: true,
                    sx: columnasDeAltoCompleto(columnGroups)
                }
                : {})}
            rows={rows || []}
            getRowClassName={rowClassName}
            loading={loading}
            checkboxSelection={checkboxSelection}
            onRowClick={(data) => {
                if (data.row.id === row?.id) {
                    setter({ status: false, row: {} })
                    return;
                }
                setter({ status: true, row: data.row })
            }}
            selectionModel={row?.id || []}
            className={classes.root}
            rowHeight={36}
            autoPageSize
            pagination
            hideFooterSelectedRowCount
            disableMultipleSelection
            disableColumnMenu
            unstable_dataSourceCache={0}
            localeText={{ ...esES.components.MuiDataGrid.defaultProps.localeText, noRowsLabel: "Sin datos" }}
        />
    );
}
